import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendUp } from "@phosphor-icons/react";

const inputCls = "h-8 px-2 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[12px]";

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div className="flex items-center gap-2">
    <span className="w-32 text-[11px] font-mono text-[var(--text-dim)]">{label}</span>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={testid} />
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-[12px] font-mono py-0.5 border-b border-[var(--border)]/30">
    <span className="text-[var(--text-dim)]">{k}</span><span className="text-[var(--text)] font-bold">{v}</span>
  </div>
);

export const SlopeCalc = () => {
  const [open, setOpen] = useState(false);

  // slope from elevations
  const [e1, setE1] = useState("");
  const [e2, setE2] = useState("");
  const [dist, setDist] = useState("");

  // elevation along grade
  const [baseEl, setBaseEl] = useState("");
  const [grade, setGrade] = useState("");
  const [station, setStation] = useState("");

  const rise = e1 !== "" && e2 !== "" ? Number(e2) - Number(e1) : null;
  const run = Number(dist) || 0;
  const slopePct = rise !== null && run > 0 ? (rise / run) * 100 : null;
  const grade1 = rise !== null && run > 0 && Math.abs(rise) > 1e-9 ? run / Math.abs(rise) : null; // H:V
  const angle = rise !== null && run > 0 ? Math.atan2(rise, run) * 180 / Math.PI : null;

  const gPct = Number(grade);
  const targetEl = baseEl !== "" && grade !== "" && station !== ""
    ? Number(baseEl) + (gPct / 100) * Number(station) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="slope-calc-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <TrendUp size={14} weight="bold" /> Slope / Grade
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-lg rounded-[4px]" data-testid="slope-calc-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><TrendUp size={18} weight="bold" className="text-[var(--primary)]" />Slope / Grade Calculator</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Compute slope, grade % and ratio from two elevations, or solve an elevation at a station along a grade.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="slope">
          <TabsList className="bg-[var(--surface-2)] rounded-[3px]">
            <TabsTrigger value="slope" data-testid="slope-tab-slope" className="text-xs">Slope from elevations</TabsTrigger>
            <TabsTrigger value="grade" data-testid="slope-tab-grade" className="text-xs">Elevation along grade</TabsTrigger>
          </TabsList>

          <TabsContent value="slope" className="flex flex-col gap-2 mt-3">
            <Field label="Elevation A" value={e1} onChange={setE1} testid="slope-e1" placeholder="start elevation" />
            <Field label="Elevation B" value={e2} onChange={setE2} testid="slope-e2" placeholder="end elevation" />
            <Field label="Horizontal distance" value={dist} onChange={setDist} testid="slope-dist" placeholder="run (ft)" />
            {slopePct !== null && (
              <div className="border border-[var(--border)] rounded-[3px] p-3 bg-[var(--surface-2)]" data-testid="slope-result">
                <Row k="Rise (Δ elevation)" v={`${rise.toFixed(3)} ft`} />
                <Row k="Slope / Grade" v={`${slopePct.toFixed(2)} %`} />
                <Row k="Ratio (H:V)" v={grade1 !== null ? `${grade1.toFixed(2)} : 1` : "—"} />
                <Row k="Angle" v={`${angle.toFixed(3)}°`} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="grade" className="flex flex-col gap-2 mt-3">
            <Field label="Base elevation" value={baseEl} onChange={setBaseEl} testid="grade-base" placeholder="elevation at station 0" />
            <Field label="Grade (%)" value={grade} onChange={setGrade} testid="grade-pct" placeholder="e.g. -2.5" />
            <Field label="Distance / station" value={station} onChange={setStation} testid="grade-station" placeholder="horizontal distance (ft)" />
            {targetEl !== null && (
              <div className="border border-[var(--border)] rounded-[3px] p-3 bg-[var(--surface-2)]" data-testid="grade-result">
                <Row k="Δ elevation" v={`${((gPct / 100) * Number(station)).toFixed(3)} ft`} />
                <Row k="Elevation at station" v={`${targetEl.toFixed(3)} ft`} />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
